import { cameraActivityQuery, cameraEventsQuery, cameraQuery, siteQuery } from '@/api/queries'
import { CameraNotesPanel } from '@/components/camera/CameraNotesPanel'
import { CameraSettingsModal } from '@/components/camera/CameraSettingsModal'
import { CameraView } from '@/components/camera/CameraView'
import { PhotoBrowser } from '@/components/camera/PhotoBrowser'
import { WhatZonooSees } from '@/components/camera/WhatZonooSees'
import { AnalysisAreaDrawer } from '@/components/drawers/AnalysisAreaDrawer'
import { AnalysisDrawer } from '@/components/drawers/AnalysisDrawer'
import { BaselineDrawer } from '@/components/drawers/BaselineDrawer'
import { DisplayCropDrawer } from '@/components/drawers/DisplayCropDrawer'
import { EventDrawer } from '@/components/drawers/EventDrawer'
import { ErrorCard, LoadingCard } from '@/components/ui/StateCards'
import { useCan } from '@/lib/auth'
import { localDateInTz } from '@/lib/format'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { t } from '@/lib/i18n'

const cameraSearchSchema = z.object({
  event: z.coerce.number().int().positive().optional(),
  baseline: z.coerce.boolean().optional(),
  analysisArea: z.coerce.boolean().optional(),
  crop: z.coerce.boolean().optional(),
  analysis: z.coerce.number().int().positive().optional(),
  // Camera Page UI/UX item 5: the Camera settings panel.
  settings: z.coerce.boolean().optional(),
  // Camera Page UI/UX item 2: an explicitly selected photo (thumbnail /
  // prev-next). Absent means "the day's default" (latest for today,
  // closest-to-noon for a past day) -- see the auto-pin effect below.
  photo: z.coerce.number().int().positive().optional(),
  // WEB-021: same shape/semantics as the site dashboard — YYYY-MM-DD in
  // the site's timezone; absent means "today".
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
})

export const Route = createFileRoute('/sites/$siteId/cameras/$cameraId')({
  validateSearch: cameraSearchSchema,
  component: CameraRoute,
})

function CameraRoute() {
  const { siteId, cameraId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/sites/$siteId/cameras/$cameraId' })
  const selectedDate = search.date ?? null

  const cameraQ = useQuery(cameraQuery(siteId, cameraId, selectedDate))
  const eventsQ = useQuery(cameraEventsQuery(siteId, cameraId, selectedDate))
  const activityQ = useQuery(cameraActivityQuery(siteId, cameraId, selectedDate))
  const siteQ = useQuery(siteQuery(siteId, selectedDate))

  // Camera Page UI/UX item 5: the existing camera.edit capability (not
  // the blanket site.edit the analysis-area/framing editors still use
  // internally) gates the one Settings control -- and the server
  // re-checks camera.edit on every analysis-hours read/write regardless
  // of what this renders (server/main.py's _CAMERA_ANALYSIS_HOURS_RE).
  const canEditCamera = useCan('camera.edit', { type: 'camera', id: cameraId })
  // Item 5: analysis area / framing / baseline are now grouped into the
  // one Settings control, so their UI-level gate is unified onto
  // camera.edit too. The underlying analysis-area/display-crop backend
  // endpoints keep their own pre-existing site.edit requirement
  // unchanged (server/main.py) -- out of scope for this package, and
  // identical in practice for both roles that exist today (admin holds
  // both capabilities via the wildcard preset; a viewer holds neither).

  const dayEvents = eventsQ.data?.events ?? [] // newest-first
  const withImages = dayEvents.filter((e) => e.has_image)
  const trueLatest = withImages[0] ?? null
  const latestAnalyzed = dayEvents.find((e) => e.analysis_state === 'analyzed') ?? null

  // ── Item 2: pin the main photo to a concrete event id so a background
  // poll never swaps the image out from under the viewer. `autoPinned`
  // seeds once from the day's default -- the most recently REVIEWED
  // photo, so the main view always has something written about it
  // rather than the raw "hasn't been reviewed yet" state (Dori,
  // 30 Sep: show what's reviewed by default; the true newest capture,
  // reviewed or not, is reachable via the "newer photo available"
  // banner / the "Latest" button / the timeline's own marker instead).
  // Falls back to the day's true latest capture only when nothing has
  // been reviewed yet at all (e.g. before the first hourly analysis of
  // the day has run) -- there is no "reviewed" photo to default to yet.
  // Then freezes; an explicit `search.photo` (thumbnail / prev-next)
  // always overrides it. Both reset when the camera or day changes. ──
  const [autoPinned, setAutoPinned] = useState<number | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset-on-change effect -- cameraId/selectedDate are the reset trigger, not read in the body
  useEffect(() => {
    setAutoPinned(null)
  }, [cameraId, selectedDate])
  useEffect(() => {
    if (search.photo !== undefined) return
    if (autoPinned !== null) return
    if (latestAnalyzed) {
      setAutoPinned(latestAnalyzed.id)
      return
    }
    // No reviewed photo yet today -- only fall back to the day's true
    // latest capture once the day's events have actually finished
    // loading (not merely "not fetched yet"), so the faster camera
    // query's last_snapshot never preempts a slower-to-load, but
    // genuinely reviewed, event from the events query.
    if (eventsQ.isSuccess && cameraQ.data?.last_snapshot) {
      setAutoPinned(cameraQ.data.last_snapshot.event_id)
    }
  }, [search.photo, autoPinned, latestAnalyzed, eventsQ.isSuccess, cameraQ.data])

  const effectiveEventId = search.photo ?? autoPinned
  const selectedEvent = dayEvents.find((e) => e.id === effectiveEventId) ?? null

  const newerAvailable =
    !!trueLatest &&
    effectiveEventId !== null &&
    trueLatest.id !== effectiveEventId &&
    (!selectedEvent ||
      new Date(trueLatest.timestamp).getTime() > new Date(selectedEvent.timestamp).getTime())

  const selectEvent = (eventId: number) =>
    navigate({ resetScroll: false, search: (prev) => ({ ...prev, photo: eventId }) })

  const viewLatest = () => {
    if (selectedDate !== null) {
      // Latest always means "today's newest", regardless of what day was
      // being browsed.
      navigate({
        resetScroll: false,
        search: (prev) => ({ ...prev, date: undefined, photo: undefined }),
      })
      setAutoPinned(null)
      return
    }
    setAutoPinned(cameraQ.data?.last_snapshot?.event_id ?? null)
    navigate({ resetScroll: false, search: (prev) => ({ ...prev, photo: undefined }) })
  }

  if (cameraQ.isPending || eventsQ.isPending) return <LoadingCard label={t('Loading camera…')} shape="camera" />
  if (cameraQ.isError)
    return <ErrorCard message={cameraQ.error.message} onRetry={cameraQ.refetch} />
  if (eventsQ.isError)
    return <ErrorCard message={eventsQ.error.message} onRetry={eventsQ.refetch} />

  const siblings = siteQ.data?.cameras ?? []
  const { prevCamera, nextCamera } = pickSiblings(siblings, cameraId)
  const todayDate = localDateInTz(cameraQ.data.site.timezone ?? null)

  const photoSrc = effectiveEventId
    ? `/api/sites/${encodeURIComponent(siteId)}/events/${effectiveEventId}/image`
    : null

  return (
    <>
      <CameraView
        detail={cameraQ.data}
        photo={{
          src: photoSrc,
          timestamp: selectedEvent?.timestamp ?? cameraQ.data.last_snapshot?.timestamp ?? null,
          createdAt: selectedEvent?.created_at ?? null,
          loading: effectiveEventId === null && !!cameraQ.data.last_snapshot,
        }}
        newerAvailable={newerAvailable}
        onViewLatest={viewLatest}
        onRefreshSnapshot={() => {
          cameraQ.refetch()
          eventsQ.refetch()
        }}
        prevCamera={prevCamera}
        nextCamera={nextCamera}
        selectedDate={selectedDate}
        onOpenSettings={
          canEditCamera
            ? () =>
                navigate({ resetScroll: false, search: (prev) => ({ ...prev, settings: true }) })
            : undefined
        }
        sightSlot={
          <WhatZonooSees selectedEvent={selectedEvent} entries={activityQ.data?.entries ?? []} />
        }
        browserSlot={
          <PhotoBrowser
            siteId={siteId}
            cameraId={cameraId}
            events={dayEvents}
            selectedEventId={effectiveEventId}
            latestEventId={trueLatest?.id ?? null}
            selectedDate={selectedDate}
            todayDate={todayDate}
            onSelectDate={(d) =>
              navigate({
                resetScroll: false,
                search: (prev) => ({ ...prev, date: d ?? undefined, photo: undefined }),
              })
            }
            onSelectEvent={selectEvent}
            onViewLatest={viewLatest}
          />
        }
        activitySlot={
          // 2 Oct 2026: camera notes are admin-only; the API only sends
          // them to users who can edit this camera's notes.
          cameraQ.data.camera.notes !== undefined ? (
            <details className="rounded-xl border border-border-subtle">
              <summary className="cursor-pointer px-4 py-3 text-sm text-foreground-muted select-none">
                {t('Notes')}
              </summary>
              <div className="px-4 pb-4">
                <CameraNotesPanel notes={cameraQ.data.camera.notes} />
              </div>
            </details>
          ) : null
        }
      />

      {search.settings && canEditCamera ? (
        <CameraSettingsModal
          siteId={siteId}
          cameraId={cameraId}
          notes={cameraQ.data.camera.notes ?? ''}
          onClose={() =>
            navigate({ resetScroll: false, search: (prev) => ({ ...prev, settings: undefined }) })
          }
          onOpenAnalysisArea={() =>
            navigate({
              resetScroll: false,
              search: (prev) => ({ ...prev, settings: undefined, analysisArea: true }),
            })
          }
          onOpenFraming={() =>
            navigate({
              resetScroll: false,
              search: (prev) => ({ ...prev, settings: undefined, crop: true }),
            })
          }
          onOpenBaseline={() =>
            navigate({
              resetScroll: false,
              search: (prev) => ({ ...prev, settings: undefined, baseline: true }),
            })
          }
        />
      ) : null}
      {search.baseline && canEditCamera ? (
        <BaselineDrawer
          siteId={siteId}
          cameraId={cameraId}
          onClose={() =>
            navigate({ resetScroll: false, search: (prev) => ({ ...prev, baseline: undefined }) })
          }
        />
      ) : null}
      {search.analysisArea && canEditCamera ? (
        <AnalysisAreaDrawer
          siteId={siteId}
          cameraId={cameraId}
          onClose={() =>
            navigate({
              resetScroll: false,
              search: (prev) => ({ ...prev, analysisArea: undefined }),
            })
          }
        />
      ) : null}
      {search.crop && canEditCamera ? (
        <DisplayCropDrawer
          siteId={siteId}
          cameraId={cameraId}
          context="grid"
          contextAspect={16 / 9}
          contextLabel="dashboard card"
          onClose={() =>
            navigate({ resetScroll: false, search: (prev) => ({ ...prev, crop: undefined }) })
          }
        />
      ) : null}
      {search.analysis !== undefined && canEditCamera ? (
        <AnalysisDrawer
          siteId={siteId}
          analysisType="batch"
          analysisId={search.analysis}
          onClose={() =>
            navigate({ resetScroll: false, search: (prev) => ({ ...prev, analysis: undefined }) })
          }
        />
      ) : null}
      {search.event !== undefined ? (
        <EventDrawer
          siteId={siteId}
          eventId={search.event}
          cameraId={cameraId}
          onClose={() =>
            navigate({ resetScroll: false, search: (prev) => ({ ...prev, event: undefined }) })
          }
          onNavigate={(next) =>
            navigate({ resetScroll: false, search: (prev) => ({ ...prev, event: next }) })
          }
        />
      ) : null}
    </>
  )
}

function pickSiblings(
  cameras: ReadonlyArray<{ camera_id: string; zone: string; display_name: string }>,
  currentId: string,
): {
  prevCamera: { camera_id: string; zone: string; display_name: string } | null
  nextCamera: { camera_id: string; zone: string; display_name: string } | null
} {
  if (cameras.length < 2) return { prevCamera: null, nextCamera: null }
  const idx = cameras.findIndex((c) => c.camera_id === currentId)
  if (idx < 0) return { prevCamera: null, nextCamera: null }
  const n = cameras.length
  const prev = cameras[(idx - 1 + n) % n]
  const next = cameras[(idx + 1) % n]
  if (!prev || !next) return { prevCamera: null, nextCamera: null }
  return {
    prevCamera: { camera_id: prev.camera_id, zone: prev.zone, display_name: prev.display_name },
    nextCamera: { camera_id: next.camera_id, zone: next.zone, display_name: next.display_name },
  }
}
